import { ComponentProps, FC, ReactNode } from "react";
import { Row, Alert } from "antd";
import { MailOutlined, EyeTwoTone, EyeInvisibleOutlined, UnlockOutlined } from "@ant-design/icons";
import { Form, Input, Checkbox } from "formik-antd";
import * as _ from "lodash";

import { Button } from "../../../elements";
import styles from "./LoginForm.module.less";
import { classname } from "@ctra/utils";

export interface LoginFormProps extends ComponentProps<typeof Form> {
  /**
   * translated labels
   */
  labels: {
    username: string;
    password: string;
    submit: string;
    rememberMe: string;
    rejected?: string | ReactNode;
  };
  /**
   * handle field blur
   * @param {string} fieldName
   */
  onFieldBlur: (fieldName: "username" | "password") => void;
  /**
   * loading state
   */
  isLoading?: boolean;
  /**
   * form extra, eg. link to other page
   */
  extra?: ReactNode;
}

/**
 * Test ids for unit testing
 * @type {{submit: string, emailField: string, passwordField: string, rememberMe: string}}
 */
export const testIds = {
  emailField: "emailField",
  passwordField: "passwordField",
  submit: "submit",
  rememberMe: "rememberMe"
};

/**
 * Login form component
 * @param {string} username
 * @param {string} password
 * @param {string} submit
 * @param {string} rememberMe
 * @param {string} rejected
 * @param {boolean | undefined} isLoading
 * @param {boolean | undefined} isRejected
 * @param {(fieldName: string) => void} onFieldBlur
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} extra
 * @param {string | undefined} className
 * @param {Pick<LoginFormProps & {children?: React.ReactNode | undefined}, "prefixCls" | "colon" | "name" | "layout" | "labelAlign" | "labelWrap" | "labelCol" | "wrapperCol" | "form" | "size" | "disabled" | "scrollToFirstError" | "requiredMark" | "hideRequiredMark" | "initialValues" | "children" | "component" | "fields" | "validateMessages" | "onValuesChange" | "onFieldsChange" | "onFinish" | "onFinishFailed" | "validateTrigger" | "preserve" | "acceptCharset" | "action" | "autoComplete" | "encType" | "method" | "noValidate" | "target" | "defaultChecked" | "defaultValue" | "suppressContentEditableWarning" | "suppressHydrationWarning" | "accessKey" | "contentEditable" | "contextMenu" | "dir" | "draggable" | "hidden" | "id" | "lang" | "placeholder" | "slot" | "spellCheck" | "style" | "tabIndex" | "title" | "translate" | "radioGroup" | "role" | "about" | "datatype" | "inlist" | "prefix" | "property" | "resource" | "typeof" | "vocab" | "autoCapitalize" | "autoCorrect" | "autoSave" | "color" | "itemProp" | "itemScope" | "itemType" | "itemID" | "itemRef" | "results" | "security" | "unselectable" | "inputMode" | "is" | "aria-activedescendant" | "aria-atomic" | "aria-autocomplete" | "aria-busy" | "aria-checked" | "aria-colcount" | "aria-colindex" | "aria-colspan" | "aria-controls" | "aria-current" | "aria-describedby" | "aria-details" | "aria-disabled" | "aria-dropeffect" | "aria-errormessage" | "aria-expanded" | "aria-flowto" | "aria-grabbed" | "aria-haspopup" | "aria-hidden" | "aria-invalid" | "aria-keyshortcuts" | "aria-label" | "aria-labelledby" | "aria-level" | "aria-live" | "aria-modal" | "aria-multiline" | "aria-multiselectable" | "aria-orientation" | "aria-owns" | "aria-placeholder" | "aria-posinset" | "aria-pressed" | "aria-readonly" | "aria-relevant" | "aria-required" | "aria-roledescription" | "aria-rowcount" | "aria-rowindex" | "aria-rowspan" | "aria-selected" | "aria-setsize" | "aria-sort" | "aria-valuemax" | "aria-valuemin" | "aria-valuenow" | "aria-valuetext" | "dangerouslySetInnerHTML" | "onCopy" | "onCopyCapture" | "onCut" | "onCutCapture" | "onPaste" | "onPasteCapture" | "onCompositionEnd" | "onCompositionEndCapture" | "onCompositionStart" | "onCompositionStartCapture" | "onCompositionUpdate" | "onCompositionUpdateCapture" | "onFocus" | "onFocusCapture" | "onBlur" | "onBlurCapture" | "onChange" | "onChangeCapture" | "onBeforeInput" | "onBeforeInputCapture" | "onInput" | "onInputCapture" | "onReset" | "onResetCapture" | "onSubmitCapture" | "onInvalid" | "onInvalidCapture" | "onLoad" | "onLoadCapture" | "onError" | "onErrorCapture" | "onKeyDown" | "onKeyDownCapture" | "onKeyPress" | "onKeyPressCapture" | "onKeyUp" | "onKeyUpCapture" | "onAbort" | "onAbortCapture" | "onCanPlay" | "onCanPlayCapture" | "onCanPlayThrough" | "onCanPlayThroughCapture" | "onDurationChange" | "onDurationChangeCapture" | "onEmptied" | "onEmptiedCapture" | "onEncrypted" | "onEncryptedCapture" | "onEnded" | "onEndedCapture" | "onLoadedData" | "onLoadedDataCapture" | "onLoadedMetadata" | "onLoadedMetadataCapture" | "onLoadStart" | "onLoadStartCapture" | "onPause" | "onPauseCapture" | "onPlay" | "onPlayCapture" | "onPlaying" | "onPlayingCapture" | "onProgress" | "onProgressCapture" | "onRateChange" | "onRateChangeCapture" | "onSeeked" | "onSeekedCapture" | "onSeeking" | "onSeekingCapture" | "onStalled" | "onStalledCapture" | "onSuspend" | "onSuspendCapture" | "onTimeUpdate" | "onTimeUpdateCapture" | "onVolumeChange" | "onVolumeChangeCapture" | "onWaiting" | "onWaitingCapture" | "onAuxClick" | "onAuxClickCapture" | "onClick" | "onClickCapture" | "onContextMenu" | "onContextMenuCapture" | "onDoubleClick" | "onDoubleClickCapture" | "onDrag" | "onDragCapture" | "onDragEnd" | "onDragEndCapture" | "onDragEnter" | "onDragEnterCapture" | "onDragExit" | "onDragExitCapture" | "onDragLeave" | "onDragLeaveCapture" | "onDragOver" | "onDragOverCapture" | "onDragStart" | "onDragStartCapture" | "onDrop" | "onDropCapture" | "onMouseDown" | "onMouseDownCapture" | "onMouseEnter" | "onMouseLeave" | "onMouseMove" | "onMouseMoveCapture" | "onMouseOut" | "onMouseOutCapture" | "onMouseOver" | "onMouseOverCapture" | "onMouseUp" | "onMouseUpCapture" | "onSelect" | "onSelectCapture" | "onTouchCancel" | "onTouchCancelCapture" | "onTouchEnd" | "onTouchEndCapture" | "onTouchMove" | "onTouchMoveCapture" | "onTouchStart" | "onTouchStartCapture" | "onPointerDown" | "onPointerDownCapture" | "onPointerMove" | "onPointerMoveCapture" | "onPointerUp" | "onPointerUpCapture" | "onPointerCancel" | "onPointerCancelCapture" | "onPointerEnter" | "onPointerEnterCapture" | "onPointerLeave" | "onPointerLeaveCapture" | "onPointerOver" | "onPointerOverCapture" | "onPointerOut" | "onPointerOutCapture" | "onGotPointerCapture" | "onGotPointerCaptureCapture" | "onLostPointerCapture" | "onLostPointerCaptureCapture" | "onScroll" | "onScrollCapture" | "onWheel" | "onWheelCapture" | "onAnimationStart" | "onAnimationStartCapture" | "onAnimationEnd" | "onAnimationEndCapture" | "onAnimationIteration" | "onAnimationIterationCapture" | "onTransitionEnd" | "onTransitionEndCapture">} rest
 * @return {JSX.Element}
 * @constructor
 */
const LoginForm: FC<LoginFormProps> & { testIds: typeof testIds } = ({
  labels: { username, password, submit, rememberMe, rejected },
  isLoading,
  onFieldBlur,
  extra = null,
  className,
  ...rest
}) => (
  <Form size="large" className={classname("ctra-auth-loginForm", styles.LoginForm, className)} {...rest}>
    <Form.Item name="username">
      <Input
        data-testid={testIds.emailField}
        data-gtm-category="Login"
        data-gtm-action="Username"
        disabled={isLoading}
        // type="email"
        name="username"
        multiple={true}
        required={true}
        placeholder={username}
        prefix={<MailOutlined />}
        onBlur={_.partial(onFieldBlur, "username")}
      />
    </Form.Item>
    <Form.Item name="password">
      <Input.Password
        data-testid={testIds.passwordField}
        data-gtm-category="Login"
        data-gtm-action="Password"
        disabled={isLoading}
        iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
        name="password"
        required={true}
        placeholder={password}
        prefix={<UnlockOutlined />}
        onBlur={_.partial(onFieldBlur, "password")}
      />
    </Form.Item>
    <Button
      block
      data-testid={testIds.submit}
      data-gtm-category="Login"
      data-gtm-action="Login button"
      type="primary"
      htmlType="submit"
      size="large"
      loading={isLoading}
      disabled={isLoading}
    >
      {submit}
    </Button>
    {rejected &&
      (_.isString(rejected) ? (
        <Alert closable showIcon type="error" message={rejected} className={styles.Extra} />
      ) : (
        rejected
      ))}
    <Row align="middle" justify="space-between" className={classname(styles.Extra, styles.Flat)}>
      <Form.Item name="rememberMe" style={{ margin: 0 }}>
        <Checkbox
          data-gtm-category="Login"
          data-gtm-action="Remember me checkbox"
          data-testid={testIds.rememberMe}
          disabled={isLoading}
          name="rememberMe"
        >
          {rememberMe}
        </Checkbox>
      </Form.Item>
      {extra}
    </Row>
  </Form>
);

LoginForm.testIds = testIds;

export default LoginForm;
